import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Shield, 
  CheckCircle, 
  AlertCircle, 
  FileText, 
  Download,
  Clock,
  TrendingUp,
  Users,
  Database,
  Eye,
  RefreshCw
} from 'lucide-react'
import ReactJsonView from 'react-json-view'

import { DSAPMockServer, Scenario, DSAPRequest, DSAPResponse } from '../mock/dsap-mock'

interface Props {
  onComplete: (data: any) => void
  selectedScenario: Scenario | null
  auditData: any
  mockServer: DSAPMockServer
  addToHistory: (request: DSAPRequest, response: DSAPResponse) => void
}

export default function Step4Compliance({
  onComplete,
  selectedScenario,
  auditData,
  mockServer,
  addToHistory
}: Props) {
  const [loading, setLoading] = useState(false)
  const [complianceData, setComplianceData] = useState<any>(null)
  const [showComplianceJson, setShowComplianceJson] = useState(false)
  const [auditLog, setAuditLog] = useState<any[]>([])
  const [showAuditStream, setShowAuditStream] = useState(false)

  const complianceRequest = useMemo(() => {
    if (!selectedScenario) return null
    
    return {
      jsonrpc: '2.0',
      method: 'dsap.checkCompliance',
      params: {
        agentWorkflowId: selectedScenario.agentId,
        userId: selectedScenario.userId,
        dataOperations: [
          {
            resource: selectedScenario.dataResource,
            operation: selectedScenario.operation,
            dataTypes: selectedScenario.context.sensitivity
          }
        ],
        regulations: ['GDPR', 'CCPA', 'SOX']
      },
      id: Date.now()
    }
  }, [selectedScenario])

  // Simulate real-time audit log updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (showAuditStream) {
        const logs = mockServer.getAuditLog()
        setAuditLog(logs)
      }
    }, 2000)

    return () => clearInterval(interval)
  }, [showAuditStream, mockServer])

  const handleRunCompliance = useCallback(async () => {
    if (!complianceRequest) return
    
    setLoading(true)
    
    try {
      const response = await mockServer.processRequest(complianceRequest)
      addToHistory(complianceRequest, response)
      
      if (response.result) {
        setComplianceData(response.result)
        // Auto-show JSON after success
        setTimeout(() => setShowComplianceJson(true), 1000)
      }
    } catch (error) {
      console.error('Compliance check failed:', error)
    } finally {
      setLoading(false)
    }
  }, [complianceRequest, mockServer, addToHistory])

  const handleDownloadReport = useCallback(() => {
    const report = {
      reportType: 'DSAP_Compliance_Report',
      generatedAt: new Date().toISOString(),
      scenario: selectedScenario?.name,
      complianceData,
      auditData,
      summary: {
        overallCompliance: complianceData?.compliant ? 'COMPLIANT' : 'NON_COMPLIANT',
        riskScore: complianceData?.riskAssessment?.riskScore,
        regulationsChecked: complianceData?.findings?.length || 0,
        dataOperations: 1,
        auditTrailId: auditData?.logEntryId
      }
    }
    
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `dsap-compliance-report-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }, [complianceData, auditData, selectedScenario])

  const handleRestart = useCallback(() => {
    window.location.reload()
  }, [])

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-dsap-100 rounded-lg flex items-center justify-center">
              <Shield className="h-6 w-6 text-dsap-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Step 4: Generate Compliance Report
            </h3>
            <p className="text-gray-600 mb-4">
              Complete the DSAP workflow by generating a comprehensive compliance report 
              that validates the data operation against multiple regulatory frameworks 
              and provides audit trail documentation.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="font-medium text-blue-900 mb-2">Workflow Summary</h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-blue-800">Server capabilities discovered</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-blue-800">Data access authorized</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-blue-800">Activity logged in audit trail</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Check */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">
          Multi-Regulation Compliance Check
        </h4>
        
        {!complianceData ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-green-600" />
            </div>
            <h5 className="text-lg font-medium text-gray-900 mb-2">
              Ready for Compliance Validation
            </h5>
            <p className="text-gray-600 mb-6">
              Analyze the complete data workflow against GDPR, CCPA, and SOX regulations.
            </p>
            <button
              onClick={handleRunCompliance}
              disabled={loading}
              className={`inline-flex items-center px-8 py-4 text-lg font-medium rounded-lg transition-all ${
                loading
                  ? 'bg-green-400 text-white cursor-wait'
                  : 'bg-green-600 text-white hover:bg-green-700 transform hover:scale-105'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3" />
                  Running Compliance Check...
                </>
              ) : (
                <>
                  <Shield className="h-5 w-5 mr-3" />
                  Run Compliance Check
                </>
              )}
            </button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {/* Compliance Overview */}
            <div className={`p-6 rounded-lg border-2 ${
              complianceData.compliant
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    complianceData.compliant ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {complianceData.compliant ? (
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    ) : (
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    )}
                  </div>
                  <div>
                    <h4 className={`text-xl font-bold ${
                      complianceData.compliant ? 'text-green-900' : 'text-red-900'
                    }`}>
                      Workflow {complianceData.compliant ? 'COMPLIANT' : 'NON-COMPLIANT'}
                    </h4>
                    <p className={`text-sm ${
                      complianceData.compliant ? 'text-green-700' : 'text-red-700'
                    }`}>
                      Overall risk: {complianceData.riskAssessment?.overallRisk} 
                      ({complianceData.riskAssessment?.riskScore}/100)
                    </p>
                  </div>
                </div>
                
                {/* Overall Score */}
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    {Math.floor(complianceData.findings?.reduce((acc: number, f: any) => acc + f.score, 0) / complianceData.findings?.length)}%
                  </div>
                  <div className="text-sm text-gray-600">Compliance Score</div>
                </div>
              </div>
            </div>

            {/* Regulation-specific Results */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {complianceData.findings?.map((finding: any) => (
                <div
                  key={finding.regulation}
                  className={`p-4 rounded-lg border ${
                    finding.status === 'compliant'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h5 className={`font-semibold ${
                      finding.status === 'compliant' ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {finding.regulation}
                    </h5>
                    <div className={`text-lg font-bold ${
                      finding.status === 'compliant' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {finding.score}%
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="text-xs text-gray-600 font-medium">Requirements Met:</div>
                    <div className="flex flex-wrap gap-1">
                      {finding.requirements?.map((req: string) => (
                        <span
                          key={req}
                          className="px-2 py-1 bg-white text-gray-700 text-xs rounded border"
                        >
                          {req.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {finding.violations?.length > 0 && (
                    <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-800">
                      <div className="font-medium mb-1">Violations:</div>
                      {finding.violations.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Required Safeguards */}
            {complianceData.requiredSafeguards?.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 mb-3 flex items-center">
                  <Shield className="h-4 w-4 mr-2" />
                  Required Safeguards
                </h5>
                <div className="flex flex-wrap gap-2">
                  {complianceData.requiredSafeguards.map((safeguard: string) => (
                    <span
                      key={safeguard}
                      className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {safeguard.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Compliance JSON */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowComplianceJson(!showComplianceJson)}
                className="text-dsap-600 hover:text-dsap-700 font-medium text-sm"
              >
                {showComplianceJson ? 'Hide' : 'Show'} Complete Compliance Report
              </button>
              
              {showComplianceJson && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4"
                >
                  <ReactJsonView
                    src={complianceData}
                    theme="monokai"
                    collapsed={1}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard={true}
                    name="complianceReport"
                  />
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Real-time Audit Dashboard */}
      {complianceData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">
              Real-Time Audit Dashboard
            </h4>
            <button
              onClick={() => setShowAuditStream(!showAuditStream)}
              className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                showAuditStream
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {showAuditStream ? (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Live Feed ON
                </>
              ) : (
                <>
                  <Clock className="h-4 w-4 mr-2" />
                  Start Live Feed
                </>
              )}
            </button>
          </div>
          
          {/* Audit Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
              <div className="text-lg font-semibold text-green-900">
                {complianceData.findings?.filter((f: any) => f.status === 'compliant').length || 0}
              </div>
              <div className="text-xs text-green-600">Compliant</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <TrendingUp className="h-6 w-6 text-blue-600 mx-auto mb-2" />
              <div className="text-lg font-semibold text-blue-900">
                {complianceData.riskAssessment?.riskScore || 0}%
              </div>
              <div className="text-xs text-blue-600">Risk Score</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <FileText className="h-6 w-6 text-purple-600 mx-auto mb-2" />
              <div className="text-lg font-semibold text-purple-900">P7Y</div>
              <div className="text-xs text-purple-600">Retention</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4 text-center">
              <Database className="h-6 w-6 text-orange-600 mx-auto mb-2" />
              <div className="text-lg font-semibold text-orange-900">1</div>
              <div className="text-xs text-orange-600">Operations</div>
            </div>
          </div>

          {/* Live Audit Stream */}
          {showAuditStream && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-gray-900 text-green-400 rounded-lg p-4 font-mono text-sm"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Live Audit Stream</span>
                </div>
                <span className="text-gray-400">
                  {new Date().toLocaleTimeString()}
                </span>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                <div>[{new Date().toISOString()}] DSAP audit record created: {auditData?.logEntryId}</div>
                <div>[{new Date().toISOString()}] Compliance check completed: PASSED</div>
                <div>[{new Date().toISOString()}] GDPR compliance: VERIFIED</div>
                <div>[{new Date().toISOString()}] CCPA compliance: VERIFIED</div>
                <div>[{new Date().toISOString()}] Audit retention: 7 years configured</div>
                <div>[{new Date().toISOString()}] Real-time monitoring: ACTIVE</div>
                {auditLog.map((entry, index) => (
                  <div key={index}>
                    [{entry.timestamp}] {entry.agentId}: {entry.operation} on {entry.resource} - {entry.outcome}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* Final Actions */}
      {complianceData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Demo Complete! 🎉
          </h4>
          <p className="text-gray-600 mb-6">
            You've successfully experienced the complete DSAP protocol workflow. The data 
            operation has been authorized, executed, logged, and validated for compliance 
            across multiple regulatory frameworks.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleDownloadReport}
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="h-5 w-5 mr-2" />
              Download Compliance Report
            </button>
            
            <button
              onClick={handleRestart}
              className="inline-flex items-center px-6 py-3 bg-dsap-600 text-white font-medium rounded-lg hover:bg-dsap-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Another Scenario
            </button>
            
            <a
              href="https://github.com/dsap-protocol/dsap"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              <FileText className="h-5 w-5 mr-2" />
              View Full Specification
            </a>
          </div>
        </div>
      )}
    </div>
  )
}